<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import Auth from './components/Auth.vue';
import CookieBanner from './components/CookieBanner.vue';
import Footer from './components/Footer.vue';
import { useAuthStore } from './stores/useAuthStore';
import { useSeasonStore } from './stores/useSeasonStore';
import backendApi from './axios/backendApi';
import { formatEventDateRange } from './functions/utils';
import { EMBED_HEIGHT_MESSAGE } from './functions/leaderboards';
import { isHostEmbed } from './embedHost';
import PoweredByDartsHub from './components/PoweredByDartsHub.vue';
import { app_url } from './config/config';
import { useThemeStore } from './stores/themeStore';

const authStore = useAuthStore();
const route = useRoute();

const showEventsDropdown = ref(false);
const showRepDropdown = ref(false);
const showAdminDropdown = ref(false);
const showMenu = ref(false);
const rotateLogo = ref(false);

const windowWidth = ref(window.innerWidth);
const isMobile = computed(() => windowWidth.value <= 768);

const EventsDropdownRef = ref<HTMLElement | null>(null);
const RepDropdownRef = ref<HTMLElement | null>(null);
const AdminDropdownRef = ref<HTMLElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);
const hamburgerRef = ref<HTMLElement | null>(null);

const isClubRep = computed(() => authStore.scopes.includes('club_representative'));
const isComiteeRep = computed(() => authStore.scopes.includes('comitee_representative'));
const isLigueRep = computed(() => authStore.scopes.includes('ligue_representative'));
const isSuperAdmin = computed(() => authStore.scopes.includes('super_admin'));
const hasRepRole = computed(() => isClubRep.value || isComiteeRep.value || isLigueRep.value);

// Pages /embed/... (classement en iframe sur un site tiers) : contenu seul, sans en-tête,
// pied de page, bandeau cookies ni modale de connexion. Avant la résolution de la première
// route, route.meta est vide : on se fie au chemin pour ne pas afficher l'en-tête un instant.
const isEmbed = computed(() => (route.matched.length
  ? Boolean(route.meta.embed)
  : window.location.pathname.startsWith('/embed/')));

// Page complète intégrée par un site hôte (?embed=true, cf. embedHost.ts) : contenu seul
// lui aussi, et hauteur envoyée au site hôte pour que l'iframe suive le contenu. Mesurée
// sur le conteneur plutôt que sur le document (body en min-height: 100vh, qui ne
// redescendrait jamais une fois l'iframe agrandie).
const hostEmbedRef = ref<HTMLElement | null>(null);
let hostEmbedObserver: ResizeObserver | null = null;
// Source en pied de page, vers la même page sur Compet (sans ?embed ni ?theme).
const hostEmbedSourceUrl = computed(() => `${app_url}${route.path}`);
const hostEmbedLightTheme = computed(() => useThemeStore().currentTheme !== 'geek');
const postHostEmbedHeight = () => {
  if (!hostEmbedRef.value) return;
  window.parent.postMessage({ type: EMBED_HEIGHT_MESSAGE, height: hostEmbedRef.value.offsetHeight }, '*');
};

// Lien(s) direct(s) vers le(s) événement(s) à gérer, quand il y en a : évite d'avoir
// à repasser par le calendrier et rouvrir la modale de l'événement à chaque fois.
// `can_manage_now` (organisateur/désigné gestionnaire de CET événement précis, ou
// super_admin, ET premium actif) plutôt que `can_manage` (qui ignore le premium
// délibérément ailleurs, cf. Calendar.vue) -- ce raccourci ne doit lister que ce qui
// est vraiment gérable tout de suite, pas des événements qui mèneraient à un mur
// premium (402) au clic. Cf. events/routes.py::_to_event_response.
const manageableEvents = ref<{ id: number; name: string; event_date: string; location: string | null; dateLabel: string }[]>([]);

const loadManageableEvents = async () => {
  if (!authStore.isAuthenticated) {
    manageableEvents.value = [];
    return;
  }
  try {
    const { data } = await backendApi.get('/events/');
    const today = new Date().toISOString().slice(0, 10);
    manageableEvents.value = (data as any[])
      .filter((e) => e.can_manage_now)
      .map((e) => ({
        id: e.id,
        name: e.full_name || e.display_name || e.name,
        event_date: e.event_date,
        location: e.location ?? null,
        dateLabel: formatEventDateRange(e.event_date, e.event_date_end),
      }))
      .sort((a, b) => {
        const aUp = a.event_date >= today;
        const bUp = b.event_date >= today;
        if (aUp !== bUp) return aUp ? -1 : 1;
        return aUp ? a.event_date.localeCompare(b.event_date) : b.event_date.localeCompare(a.event_date);
      });
  } catch {
    manageableEvents.value = [];
  }
};

const eventMeta = (ev: { dateLabel: string; location: string | null }) =>
  [ev.dateLabel, ev.location].filter(Boolean).join(' · ');

const toggleEventsDropdown = () => {
  showEventsDropdown.value = !showEventsDropdown.value;
  if (showEventsDropdown.value) {
    showRepDropdown.value = false;
    showAdminDropdown.value = false;
  }
};

const toggleRepDropdown = () => {
  showRepDropdown.value = !showRepDropdown.value;
  if (showRepDropdown.value) {
    showEventsDropdown.value = false;
    showAdminDropdown.value = false;
  }
};

const toggleAdminDropdown = () => {
  showAdminDropdown.value = !showAdminDropdown.value;
  if (showAdminDropdown.value) {
    showEventsDropdown.value = false;
    showRepDropdown.value = false;
  }
};

const closeAllDropdowns = () => {
  showEventsDropdown.value = false;
  showRepDropdown.value = false;
  showAdminDropdown.value = false;
};

const toggleMenu = () => {
  const newShowMenu = !showMenu.value;
  showMenu.value = newShowMenu;
  rotateLogo.value = newShowMenu;
  if (hamburgerRef.value) {
    hamburgerRef.value.classList.toggle('open', newShowMenu);
  }
  if (!newShowMenu) {
    closeAllDropdowns();
  }
};

const handleClickOutside = (event: MouseEvent) => {
  if (showEventsDropdown.value && EventsDropdownRef.value && !EventsDropdownRef.value.contains(event.target as Node)) {
    showEventsDropdown.value = false;
  }
  if (showRepDropdown.value && RepDropdownRef.value && !RepDropdownRef.value.contains(event.target as Node)) {
    showRepDropdown.value = false;
  }
  if (showAdminDropdown.value && AdminDropdownRef.value && !AdminDropdownRef.value.contains(event.target as Node)) {
    showAdminDropdown.value = false;
  }
  if (isMobile.value && showMenu.value && menuRef.value && !menuRef.value.contains(event.target as Node) && (!hamburgerRef.value || !hamburgerRef.value.contains(event.target as Node))) {
    toggleMenu();
  }
};

const handleResize = () => {
  windowWidth.value = window.innerWidth;
  if (!isMobile.value && showMenu.value) {
    showMenu.value = false;
    rotateLogo.value = false;
    closeAllDropdowns();
  }
};

// Saison courante depuis Dartshub (/seasons), comme CMER/LSEF -- sinon getCurrentSeason()
// reste sur le repli calculé localement (bascule au 1er août), qui peut diverger des dates
// de saison réelles. Lancé dès le setup : les pages montent avant ce composant.
useSeasonStore().load();

onMounted(() => {
  if (isHostEmbed && hostEmbedRef.value) {
    hostEmbedObserver = new ResizeObserver(postHostEmbedHeight);
    hostEmbedObserver.observe(hostEmbedRef.value);
  }
  if (isEmbed.value || isHostEmbed) return;
  loadManageableEvents();
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  hostEmbedObserver?.disconnect();
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', handleResize);
});

watch(() => authStore.isAuthenticated, () => {
  if (!isEmbed.value && !isHostEmbed) loadManageableEvents();
});
</script>

<template>
  <main v-if="isHostEmbed" class="host-embed" ref="hostEmbedRef">
    <router-view></router-view>
    <footer class="host-embed-footer">
      <PoweredByDartsHub :href="hostEmbedSourceUrl" :light="hostEmbedLightTheme" />
    </footer>
  </main>
  <router-view v-else-if="isEmbed"></router-view>
  <div v-else class="app-shell">
    <header class="header">
      <div class="top-section">
        <RouterLink to="/" class="nav-link logo-link" @click="closeAllDropdowns()">
          <img src="./assets/logo-icon.png" alt="Logo DartsHub" class="logo" :class="{ rotate: rotateLogo }" />
        </RouterLink>
        <ul v-if="!isMobile" class="nav-links">
          <li>
            <RouterLink to="/" class="nav-link" @click="closeAllDropdowns()">Calendrier</RouterLink>
          </li>
          <li v-if="manageableEvents.length === 1">
            <RouterLink :to="`/event-inscriptions/${manageableEvents[0].id}`" class="nav-link"
              :title="[manageableEvents[0].name, eventMeta(manageableEvents[0])].filter(Boolean).join(' · ')"
              @click="closeAllDropdowns()">Gérer mon événement</RouterLink>
          </li>
          <li v-else-if="manageableEvents.length > 1" class="dropdown" ref="EventsDropdownRef">
            <a href="#" class="nav-link" @click.prevent="toggleEventsDropdown">Gérer mes événements</a>
            <ul v-show="showEventsDropdown" class="dropdown-menu">
              <li v-for="ev in manageableEvents" :key="ev.id">
                <RouterLink :to="`/event-inscriptions/${ev.id}`" class="dropdown-item" @click="closeAllDropdowns()">
                  <span class="dropdown-item-name">{{ ev.name }}</span>
                  <span class="dropdown-item-meta">{{ eventMeta(ev) }}</span>
                </RouterLink>
              </li>
            </ul>
          </li>
          <li v-if="hasRepRole" class="dropdown" ref="RepDropdownRef">
            <a href="#" class="nav-link" @click.prevent="toggleRepDropdown">Mes structures</a>
            <ul v-show="showRepDropdown" class="dropdown-menu">
              <li v-if="isClubRep">
                <RouterLink to="/mon-club" class="dropdown-item" @click="closeAllDropdowns()">Mon club</RouterLink>
              </li>
              <li v-if="isComiteeRep">
                <RouterLink to="/mon-comite" class="dropdown-item" @click="closeAllDropdowns()">Mon comité</RouterLink>
              </li>
              <li v-if="isLigueRep">
                <RouterLink to="/ma-ligue" class="dropdown-item" @click="closeAllDropdowns()">Ma ligue</RouterLink>
              </li>
            </ul>
          </li>
          <li v-if="isSuperAdmin" class="dropdown" ref="AdminDropdownRef">
            <a href="#" class="nav-link" @click.prevent="toggleAdminDropdown">Outils admins</a>
            <ul v-show="showAdminDropdown" class="dropdown-menu">
              <li>
                <RouterLink to="/admin/import-classement" class="dropdown-item" @click="closeAllDropdowns()">Import
                  classement</RouterLink>
              </li>
              <li>
                <RouterLink to="/admin/baremes" class="dropdown-item" @click="closeAllDropdowns()">Barèmes</RouterLink>
              </li>
            </ul>
          </li>
          <li>
            <RouterLink to="/classement" class="nav-link" @click="closeAllDropdowns()">Classements</RouterLink>
          </li>
          <li>
            <RouterLink to="/services" class="nav-link" @click="closeAllDropdowns()">Services</RouterLink>
          </li>
        </ul>
        <div v-if="!isMobile && authStore.isAuthenticated" class="social-icons">
          <a class="social-icon" @click="authStore.openAuthModal()"><i class="fa-solid fa-user"></i></a>
        </div>
        <button v-if="!isMobile && !authStore.isAuthenticated" class="auth-button"
          @click="authStore.openAuthModal">Connexion</button>
        <button v-if="isMobile" class="hamburger" aria-label="Toggle menu" @click="toggleMenu()" ref="hamburgerRef">
          <span class="hamburger-line line-1"></span>
          <span class="hamburger-line line-2"></span>
          <span class="hamburger-line line-3"></span>
        </button>
      </div>
      <!-- Mobile navigation menu inside header -->
      <ul v-if="isMobile && showMenu" class="nav-links mobile" ref="menuRef">
        <li>
          <RouterLink to="/" class="nav-link" @click="toggleMenu()">Calendrier</RouterLink>
        </li>
        <li v-if="manageableEvents.length === 1">
          <RouterLink :to="`/event-inscriptions/${manageableEvents[0].id}`" class="nav-link" @click="toggleMenu()">
            Gérer mon événement</RouterLink>
        </li>
        <li v-else-if="manageableEvents.length > 1" class="dropdown">
          <a href="#" class="nav-link" @click.prevent="toggleEventsDropdown">Gérer mes événements</a>
          <ul v-show="showEventsDropdown" class="dropdown-menu">
            <li v-for="ev in manageableEvents" :key="ev.id">
              <RouterLink :to="`/event-inscriptions/${ev.id}`" class="dropdown-item" @click="toggleMenu()">
                <span class="dropdown-item-name">{{ ev.name }}</span>
                <span class="dropdown-item-meta">{{ eventMeta(ev) }}</span>
              </RouterLink>
            </li>
          </ul>
        </li>
        <li v-if="hasRepRole" class="dropdown">
          <a href="#" class="nav-link" @click.prevent="toggleRepDropdown">Mes structures</a>
          <ul v-show="showRepDropdown" class="dropdown-menu">
            <li v-if="isClubRep">
              <RouterLink to="/mon-club" class="dropdown-item" @click="toggleMenu()">Mon club</RouterLink>
            </li>
            <li v-if="isComiteeRep">
              <RouterLink to="/mon-comite" class="dropdown-item" @click="toggleMenu()">Mon comité</RouterLink>
            </li>
            <li v-if="isLigueRep">
              <RouterLink to="/ma-ligue" class="dropdown-item" @click="toggleMenu()">Ma ligue</RouterLink>
            </li>
          </ul>
        </li>
        <li v-if="isSuperAdmin" class="dropdown">
          <a href="#" class="nav-link" @click.prevent="toggleAdminDropdown">Outils admins</a>
          <ul v-show="showAdminDropdown" class="dropdown-menu">
            <li>
              <RouterLink to="/admin/import-classement" class="dropdown-item" @click="toggleMenu()">Import classement
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/admin/baremes" class="dropdown-item" @click="toggleMenu()">Barèmes</RouterLink>
            </li>
          </ul>
        </li>
        <li>
          <RouterLink to="/classement" class="nav-link" @click="toggleMenu()">Classements</RouterLink>
        </li>
        <li>
          <RouterLink to="/services" class="nav-link" @click="toggleMenu()">Services</RouterLink>
        </li>
        <li v-if="authStore.isAuthenticated">
          <a class="nav-link" @click="authStore.openAuthModal(); toggleMenu()"><i class="fa-solid fa-user"></i></a>
        </li>
        <li v-if="!authStore.isAuthenticated">
          <a class="nav-link" @click="authStore.openAuthModal(); toggleMenu()">Connexion</a>
        </li>
      </ul>
    </header>

    <!-- Auth modal -->
    <div v-if="authStore.showAuthModal" class="modal-overlay" @click.self="authStore.closeAuthModal">
      <Auth />
    </div>

    <main class="app-content" :class="{ wide: route.meta.wide }">
      <router-view></router-view>
    </main>

    <Footer />
    <CookieBanner />
  </div>
</template>

<style scoped>
.app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding-top: 125px;
  box-sizing: border-box;
  background: var(--color-bg);
  color: var(--color-fg);
  font-family: var(--font-main);
}

.app-content {
  flex: 1;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 1.5rem 1rem;
  box-sizing: border-box;
}

/* Intégré par un site hôte : la largeur est celle de l'iframe, le site hôte gère les marges. */
.host-embed {
  padding: 0.5rem 0;
  background: var(--color-bg);
  color: var(--color-fg);
  font-family: var(--font-main);
}

.host-embed-footer {
  margin-top: 1.25rem;
  text-align: center;
  font-size: 0.8rem;
}

/* Pages de classement (meta.wide) : tableaux larges, noms sur une seule ligne. */
.app-content.wide {
  max-width: 1800px;
}

/* Mobile : on laisse juste un fin liseré sur les côtés pour exploiter toute la largeur. */
@media (max-width: 768px) {
  .app-content {
    padding: 1rem 6px;
  }
}

/* Auth modal overlay */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  z-index: 2000;
}

/* Header layout */
.header {
  background-color: var(--color-bg);
  padding: 1.25rem 2rem;
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 1000;
  box-shadow: 0 2px 15px var(--color-fg-darker);
  box-sizing: border-box;
  transition: height 0.3s ease;
}

.top-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 50px;
  width: 100%;
  position: relative;
  overflow: visible;
}

/* Logo */
.logo {
  height: 130px;
  transform: translateY(50px);
}

.logo-link {
  display: flex;
  align-items: center;
}

/* Auth button */
.auth-button {
  background: var(--color-fg-darker);
  border: none;
  color: white;
  padding: 10px 20px;
  margin-top: 8px;
  border-radius: var(--radius);
  cursor: pointer;
  font-weight: 600;
  transition: background 0.3s;
}

.auth-button:hover {
  background: var(--color-fg-darker);
  color: var(--color-fg);
}

.social-icon {
  cursor: pointer;
  color: var(--color-fg-darker);
}

.social-icon:hover {
  color: var(--color-fg);
}

/* Hamburger button */
.hamburger {
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
  width: 40px;
  height: 40px;
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
}

.hamburger-line {
  display: block;
  width: 24px;
  height: 3px;
  background: var(--color-fg-darker);
  margin: 4px auto;
  transition: all 0.3s ease-in-out;
}

.hamburger.open .line-1 {
  transform: translateY(7px) rotate(45deg);
}

.hamburger.open .line-2 {
  opacity: 0;
}

.hamburger.open .line-3 {
  transform: translateY(-7px) rotate(-45deg);
}

/* Navigation links */
.nav-links {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  gap: 25px;
  cursor: pointer;
  flex-grow: 1;
  justify-content: center;
}

.nav-link {
  text-decoration: none;
  color: var(--color-fg-darker);
  font-size: 16px;
  font-weight: 600;
  transition: color 0.3s;
}

.nav-link:hover {
  color: var(--color-fg);
}

/* Dropdown styles */
.dropdown {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: 120%;
  margin-top: 10px;
  left: 0;
  background: var(--color-bg);
  border: 1px solid var(--color-fg);
  border-radius: var(--radius);
  color: var(--color-fg-darker);
  list-style: none;
  padding: 10px 0;
  margin: 0;
  min-width: 200px;
  z-index: 1000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.dropdown-item {
  display: block;
  padding: 10px 20px;
  text-decoration: none;
  color: var(--color-fg);
  transition: background 0.3s;
}

.dropdown-item:hover {
  color: var(--color-accent);
}

.dropdown-item-name {
  display: block;
  white-space: nowrap;
}

.dropdown-item-meta {
  display: block;
  font-size: 0.75rem;
  color: var(--color-fg-darker);
  white-space: nowrap;
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .header {
    padding: 10px;
  }

  .top-section {
    justify-content: center;
    align-items: center;
  }

  .logo {
    height: 90px;
    transform: translateY(35px);
    transition: transform 0.5s ease;
  }

  .logo.rotate {
    animation: rotate360 0.5s ease forwards;
  }

  @keyframes rotate360 {
    from {
      transform: translateY(35px) rotate(0deg);
    }

    to {
      transform: translateY(35px) rotate(360deg);
    }
  }

  .logo-link {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .hamburger {
    display: block;
    position: absolute;
    top: 50%;
    right: 20px;
    transform: translateY(-50%);
  }

  .nav-links {
    display: none;
  }

  .social-icons,
  .auth-button {
    display: none;
  }

  .nav-links.mobile {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background: var(--color-bg);
    margin-top: 40px;
    padding: 20px;
    width: 100%;
    box-sizing: border-box;
  }

  .nav-links.mobile li {
    width: 100%;
  }

  .dropdown-menu {
    position: static;
    border: none;
    box-shadow: none;
    padding-left: 20px;
    width: 100%;
  }

  .dropdown-item-name,
  .dropdown-item-meta {
    white-space: normal;
  }

  .social-icons {
    margin: 10px 0;
  }

  .social-icon {
    font-size: 1.25rem;
  }
}
</style>
